import React from 'react';
import { IntlShape } from "react-intl";

import { FormattedMessage, formatCurrency, formatDecimal, formatPercentage } from '@futrli/localisation';

// TODO - Some of the fallbacks in here in case of missing values aren't perfect
//        and could stand to be revisited.

// TODO - Convert switch statements to maps (with default formatters for each type of value)


export interface TNEMessageData {
  id: string;
  values: TNEMessageValues;
}

export enum TNEAccountCategory {
  Income = 'INCOME',
  Expense = 'EXPENSE',
  Asset = 'ASSET',
  Liability = 'LIABILITY',
  Equity = 'EQUITY',
}

export interface TNEMessageValues {
  forecast_amount?:  number;
  change_percent?:   number;
  count?:            number;
  journal_month?:    string;             // ISO date string (only month is used)
  journal_months?:   string[];           // Array of ISO date strings (only month is used)
  account_name?:     string;             // User-entered account name
  account_category?: TNEAccountCategory; // Account category
}


/**
 * Generic This Number Explained message component
 *
 * @param {string} message_id - Unique id for the message to display, supplied by the backend
 * @param {TNEMessageValues} values - Values to substitute into the message, supplied by the backend
 * @param {IntlShape} intl
 * @param {string} currency_code - ISO 3-letter currency code
 *
 * @returns {JSX.Element}
 *
 * NOTE: Message parameters passed in via values are defined in the backend (see https://github.com/FUTRLI/predictor).
 *       Do not alter the values fields (or their types) in a FormattedMessage without also altering the backend code
 *       to match.
 *
 * NOTE: Message IDs defined here should start with "tne."
 *
 * NOTE: DO NOT REMOVE MESSAGES OR ALTER FormattedMessage IDS WITHOUT VERIFYING THAT THEY ARE NO LONGER USED BY
 *       PREDICTOR (see https://github.com/FUTRLI/predictor)
 */
export function thisNumberExplainedMessage( // eslint-disable-line complexity
  message_id: string,
  values: TNEMessageValues,
  intl: IntlShape,
  currency_code: string,
): JSX.Element {
  switch (message_id) {
    case 'tne.annual_journals':
      /**
       * Recurring annual journal prediction
       */
      return (
        <FormattedMessage
          id="tne.annual_journals"
          description="Recurring annual journal prediction"
          defaultMessage="Annual journal of {forecast_amount} each {journal_month} to {account_name}."
          values={{
            forecast_amount: formatCurrency(values.forecast_amount ?? 0, intl, currency_code),
            journal_month: intl.formatDate(forceLocalDate(values.journal_month), {month: 'long'}),
            account_name: values.account_name ?? "n/a",
          }}
        />
      )

    case 'tne.default_account_ignore':
      /**
       * No predictions were made for this account
       */
      return (
        <FormattedMessage
          id="tne.default_account_ignore"
          description="No predictions were made for this account"
          defaultMessage="Predictions are not created for {account_name}."
          values={{
            account_name: values.account_name ?? "n/a",
          }}
        />
      )

    case 'tne.default_account_last_month':
      /**
       * Last month's activity predicted to continue
       */
      return (
        <FormattedMessage
          id="tne.default_account_last_month"
          description="Last month's activity predicted to continue"
          defaultMessage="Predicted continuance of last month's activity."
        />
      )

    case 'tne.default_account_last_year':
      /**
       * Seasonal activity predicted to continue
       */
      return (
        <FormattedMessage
          id="tne.default_account_last_year"
          description="Seasonal activity predicted to continue"
          defaultMessage="Predicted repeat of last year's activity."
        />
      )

    case 'tne.default_account_monthly':
      /**
       * Recent month's activity predicted to continue
       */
      return (
        <FormattedMessage
          id="tne.default_account_monthly"
          description="Recent month's activity predicted to continue"
          defaultMessage="Predicted continuance of last month's activity."
        />
      )

    case 'tne.default_account_monthly_no_spikes':
      /**
       * Prediction based on consistent regular monthly activity
       */
      return (
        <FormattedMessage
          id="tne.default_account_monthly_no_spikes"
          description="Prediction based on consistent regular monthly activity"
          defaultMessage="Predicted continuance of last month's activity."
        />
      )

    case 'tne.default_account_monthly_with_one_spike':
      /**
       * Prediction based on consistent regular monthly activity with a single spike
       */
      return (
        <FormattedMessage
          id="tne.default_account_monthly_with_one_spike"
          description="Prediction based on consistent regular monthly activity with a single spike"
          defaultMessage="Predicted continuance of last month's activity, repeating higher levels of activity detected last {journal_month}."
          values={{
            journal_month: intl.formatDate(forceLocalDate(values.journal_month), {month: 'long'}),
          }}
        />
      )

    case 'tne.default_account_monthly_with_multiple_spikes':
      /**
       * Prediction based on consistent regular monthly activity with a more than one spike
       */
      return (
        <FormattedMessage
          id="tne.default_account_monthly_with_multiple_spikes"
          description="Prediction based on consistent regular monthly activity with a more than one spike"
          defaultMessage="Predicted continuance of last month's activity, repeating higher levels of activity detected last {journal_months}."
          values={{
            journal_months: intl.formatList(
              values.journal_months ? values.journal_months.map(s => intl.formatDate(forceLocalDate(s), {month: 'long'})): [],
              {type: 'conjunction'},
            )
          }}
        />
      )

    case 'tne.default_account_quarterly':
      /**
       * Prediction based upon last quarter's activity
       */
      return (
        <FormattedMessage
          id="tne.default_account_quarterly"
          description="Prediction based upon last quarter's activity"
          defaultMessage="Predicted continuance of last quarter's activity."
        />
      )

    case 'tne.default_account_three_month_average':
      /**
       * Last 3 months average activity predicted to continue
       */
      return (
        <FormattedMessage
          id="tne.default_account_three_month_average"
          description="Last 3 months average activity predicted to continue"
          defaultMessage="Predicted continuance of recent 3 months average activity."
        />
      )

    case 'tne.default_account_twelve_month_average':
      /**
       * Last 12 months average activity predicted to continue
       */
      return (
        <FormattedMessage
          id="tne.default_account_twelve_month_average"
          description="Last 12 months average activity predicted to continue"
          defaultMessage="Predicted continuance of last 12 months average activity plus {change_percent} to cover inflation."
          values={{
            change_percent: formatPercentage(values.change_percent ?? 0, intl)
          }}
        />
      )

    case 'tne.fortnightly_journals':
      /**
       * Recurring fortnightly activity predicted to continue
       */
      return (
        <FormattedMessage
          id="tne.fortnightly_journals"
          description="Recurring fortnightly activity predicted to continue"
          defaultMessage="Fortnightly journal of {forecast_amount} to {account_name}."
          values={{
            forecast_amount: formatCurrency(values.forecast_amount ?? 0, intl, currency_code),
            account_name: values.account_name ?? "n/a",
          }}
        />
      )

    case 'tne.ignore1':
      /**
       * No predictions will be made for accounts in this category
       */
      return (
        <FormattedMessage
          id="tne.ignore1"
          description="No predictions were made for this account"
          defaultMessage="Predictions are not created for {account_category} accounts."
          values={{
            account_category: values.account_category ? thisNumberExplainedCategory(values.account_category, intl) : "n/a",
          }}
        />
      )

    case 'tne.ignore2':
      /**
       * Predictions will not be made for accounts with no recent activity
       */
      return (
        <FormattedMessage
          id="tne.ignore2"
          description="Predictions will not be made for accounts with no recent activity"
          defaultMessage="No transactional activity for the past {count} days."
          values={{
            count: formatDecimal(values.count ?? 0, intl, 0, 0)
          }}
        />
      )

    case 'tne.ignore3.zero_transactions':
      /**
       * Predictions will not be made as the account has no recent transactions
       */
      return (
        <FormattedMessage
          id="tne.ignore3.zero_transactions"
          description="Predictions will not be made as the account has no recent transactions"
          defaultMessage="No transactional activity for the past year."
        />
      )

    case 'tne.ignore3.insufficient_transactions':
      /**
       * Predictions will not be made as the account does not have enough recent transactions
       */
      return (
        <FormattedMessage
          id="tne.ignore3.insufficient_transactions"
          description="Predictions will not be made as the account does not have enough recent transactions"
          defaultMessage="Insufficient transactional activity over the past year for a pattern to be detected."
        />
      )

    case 'tne.ignore4':
      /**
       * Predictions will not be made as the account does not have any recent activity
       */
      return (
        <FormattedMessage
          id="tne.ignore4"
          description="Predictions will not be made as the account does not have any recent activity"
          defaultMessage="No recent activity detected."
        />
      )

    case 'tne.last_years_actuals_growth':
      /**
       * Prediction based on high positive growth
       */
      return (
        <FormattedMessage
          id="tne.last_years_actuals_growth"
          description="Prediction based on high positive growth"
          defaultMessage="High year on year growth forecasted to continue at a conservative {change_percent}."
          values={{
            change_percent: formatPercentage(values.change_percent ?? 0, intl)
          }}
        />
      )

    case 'tne.last_years_actuals_reduction':
      /**
       * Prediction based on high negative growth
       */
      return (
        <FormattedMessage
          id="tne.last_years_actuals_reduction"
          description="Prediction based on high negative growth"
          defaultMessage="High year on year reduction forecasted to continue at a conservative {change_percent}."
          values={{
            change_percent: formatPercentage(values.change_percent ?? 0, intl)
          }}
        />
      )

    case 'tne.mj_ignore':
      /**
       * No predictions made as manual journal activity is not consistent
       */
      return (
        <FormattedMessage
          id="tne.mj_ignore"
          description="No predictions made as manual journal activity is not consistent"
          defaultMessage="Ignored manual journal activity as no consistent pattern detected."
        />
      )

    case 'tne.monthly_journals':
      /**
       * Recurring monthly activity predicted to continue
       */
      return (
        <FormattedMessage
          id="tne.monthly_journals"
          description="Recurring monthly activity predicted to continue"
          defaultMessage="Monthly journal of {forecast_amount} to {account_name}."
          values={{
            forecast_amount: formatCurrency(values.forecast_amount ?? 0, intl, currency_code),
            account_name: values.account_name ?? "n/a",
          }}
        />
      )

    case 'tne.profit_and_loss_ignore':
      /**
       * Ignoring profit and loss adjustments when making predictions
       */
      return (
        <FormattedMessage
          id="tne.profit_and_loss_ignore"
          description="Ignoring profit and loss adjustments when making predictions"
          defaultMessage="Ignored possible P&L adjustment journals."
        />
      )

    case 'tne.steady':
      /**
       * Prediction based on steady repeated activity
       */
      return (
        <FormattedMessage
          id="tne.steady"
          description="Prediction based on steady repeated activity"
          defaultMessage="Predicted continuance of steady repeated activity detected over last {count} months."
          values={{
            count: formatDecimal(values.count ?? 0, intl, 0, 0),
          }}
        />
      )

    case 'tne.three_month_average':
      /**
       * Prediction based on last 3 months average
       */
      return (
        <FormattedMessage
          id="tne.three_month_average"
          description="Prediction based on last 3 months average"
          defaultMessage="New account activity. {forecast_amount} prediction based on an average of the last 3 months."
          values={{
            forecast_amount: formatCurrency(values.forecast_amount ?? 0, intl, currency_code),
          }}
        />
      )

    case 'tne.two_year_average_decrease':
      /**
       * Prediction based on a two-year average with negative growth
       */
      return (
        <FormattedMessage
          id="tne.two_year_average_decrease"
          description="Prediction based on a two-year average with negative growth"
          defaultMessage="Predicted continuance of {change_percent} average year on year decrease."
          values={{
            change_percent: intl.formatNumber(values.change_percent ?? 0, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 1,
              style: 'percent'
            })
          }}
        />
      )

    case 'tne.two_year_average_decrease_with_spikes':
      /**
       * Prediction based on a two-year average with negative growth but with higher activity in some months
       */
      return (
        <FormattedMessage
          id="tne.two_year_average_decrease_with_spikes"
          description="Prediction based on a two-year average with negative growth but with higher activity in some months"
          defaultMessage="Predicted continuance of {change_percent} average year on year decrease, factoring in higher levels of activity detected last {journal_months}."
          values={{
            change_percent: intl.formatNumber(values.change_percent ?? 0, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 1,
              style: 'percent'
            }),
            journal_months: intl.formatList(
              values.journal_months ? values.journal_months.map(s => intl.formatDate(forceLocalDate(s), {month: 'long'})): [],
              {type: 'conjunction'},
            ),
          }}
        />
      )

    case 'tne.two_year_average_increase':
      /**
       * Prediction based on a two-year average with positive growth
       */
      return (
        <FormattedMessage
          id="tne.two_year_average_increase"
          description="Prediction based on a two-year average with positive growth"
          defaultMessage="Predicted continuance of {change_percent} average year on year increase."
          values={{
            change_percent: intl.formatNumber(values.change_percent ?? 0, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 1,
              style: 'percent'
            })
          }}
        />
      )

    case 'tne.two_year_average_increase_with_spikes':
      /**
       * Prediction based on a two-year average with positive growth but with higher activity in some months
       */
      return (
        <FormattedMessage
          id="tne.two_year_average_increase_with_spikes"
          description="Prediction based on a two-year average with positive growth but with higher activity in some months"
          defaultMessage="Predicted continuance of {change_percent} average year on year increase, factoring in higher levels of activity detected last {journal_months}."
          values={{
            change_percent: intl.formatNumber(values.change_percent ?? 0, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 1,
              style: 'percent'
            }),
            journal_months: intl.formatList(
              values.journal_months ? values.journal_months.map(s => intl.formatDate(forceLocalDate(s), {month: 'long'})): [],
              {type: 'conjunction'},
            ),
          }}
        />
      )

    case 'tne.two_year_average_negligible_decrease':
      /**
       * Prediction based on a two-year average with very small negative growth
       */
      return (
        <FormattedMessage
          id="tne.two_year_average_negligible_decrease"
          description="Prediction based on a two-year average with very small negative growth"
          defaultMessage="Predicted continuance of negligible average year on year decrease."
        />
      )

    case 'tne.two_year_average_negligible_decrease_with_spikes':
      /**
       * Prediction based on a two-year average with very small negative growth but with higher activity in some months
       */
      return (
        <FormattedMessage
          id="tne.two_year_average_negligible_decrease_with_spikes"
          description="Prediction based on a two-year average with very small negative growth but with higher activity in some months"
          defaultMessage="Predicted continuance of negligible average year on year decrease, factoring in higher levels of activity detected last {journal_months}."
          values={{
            journal_months: intl.formatList(
              values.journal_months ? values.journal_months.map(s => intl.formatDate(forceLocalDate(s), {month: 'long'})): [],
              {type: 'conjunction'},
            ),
          }}
        />
      )

    case 'tne.two_year_average_negligible_increase':
      /**
       * Prediction based on a two-year average with very small positive growth
       */
      return (
        <FormattedMessage
          id="tne.two_year_average_negligible_increase"
          description="Prediction based on a two-year average with very small positive growth"
          defaultMessage="Predicted continuance of negligible average year on year increase."
        />
      )

    case 'tne.two_year_average_negligible_increase_with_spikes':
      /**
       * Prediction based on a two-year average with very small positive growth but with higher activity in some months
       */
      return (
        <FormattedMessage
          id="tne.two_year_average_negligible_increase_with_spikes"
          description="Prediction based on a two-year average with very small positive growth but with higher activity in some months"
          defaultMessage="Predicted continuance of negligible average year on year increase, factoring in higher levels of activity detected last {journal_months}."
          values={{
            journal_months: intl.formatList(
              values.journal_months ? values.journal_months.map(s => intl.formatDate(forceLocalDate(s), {month: 'long'})): [],
              {type: 'conjunction'},
            ),
          }}
        />
      )

    case 'tne.weekly_journals':
      /**
       * Recurring weekly activity predicted to continue
       */
      return (
        <FormattedMessage
          id="tne.weekly_journals"
          description="Recurring weekly activity predicted to continue"
          defaultMessage="Weekly journal of {forecast_amount} to {account_name}."
          values={{
            forecast_amount: formatCurrency(values.forecast_amount ?? 0, intl, currency_code),
            account_name: values.account_name ?? "n/a",
          }}
        />
      )

    default:
      /**
       * Unrecognised message
       */
      return (
        <FormattedMessage
          id="tne.unrecognised_message"
          description="Unrecognised This Number Explained message"
          defaultMessage="Unrecognised message ({message_id})"
          values={{
            message_id,
          }}
        />
      )
  }
}

/**
 * Translate a category name.  This *should* be safe-ish for now as the category names
 * are nouns.
 *
 * If our categories change from the current fixed names, or we start supporting languages
 * with more exotic grammars, then this will need to be revisited.
 *
 * @param {AccountCategory} category - Account category supplied by the backend (defined in https://github.com/FUTRLI/predictor)
 * @param {IntlShape} intl
 *
 * @returns {string}
 */
function thisNumberExplainedCategory(category: TNEAccountCategory, intl: IntlShape): string {
  switch (category.toUpperCase()) {
    case TNEAccountCategory.Asset:
      return intl.formatMessage({
        id: "tne.category.asset",
        description: "Asset account category",
        defaultMessage: "ASSET",
      })
    case TNEAccountCategory.Equity:
      return intl.formatMessage({
        id: "tne.category.equity",
        description: "Equity account category",
        defaultMessage: "EQUITY",
      })
    case TNEAccountCategory.Expense:
      return intl.formatMessage({
        id: "tne.category.expense",
        description: "Expense account category",
        defaultMessage: "EXPENSE",
      })
    case TNEAccountCategory.Liability:
      return intl.formatMessage({
        id: "tne.category.liability",
        description: "Liability account category",
        defaultMessage: "LIABILITY",
      })
    case TNEAccountCategory.Income:
      return intl.formatMessage({
        id: "tne.category.income",
        description: "Income account category",
        defaultMessage: "INCOME",
      })
    default:
      return category
  }
}

/**
 * Converts a date string like `2020-04` to a local date offset against the browser's timezone.
 * This is required as JS assumes date strings without timezones are UTC and converts to local.
 * E.g. in a -0300 timezone new Date('2020-04') will return 2020-03-30 21:00 -0300
 * This function would return 2020-04-01 00:00 -0300
 *
 * TODO: This method was shamelessly copied from packages/predict/src/utils/dates.ts ... we may
 *       want to import it from there in future, or move both methods to a new shared location
 *       and merge them into one.
 *
 * @param {string} date - expected to be an ISO date string "YYYY-MM-DD"
 *
 * @returns {Date}
 */
export function forceLocalDate(date: string | undefined): Date {
  if (!date) {
    date = "1900-01-01"
  }
  const [year, month, day = '1'] = date.split('-');
  const localDate = new Date(date);
  localDate.setFullYear(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
  localDate.setHours(0, 0, 0);
  return localDate;
}
